
import axios from 'axios';
import moment from 'moment';
import fileDownload from 'js-file-download';

export default {
  name: 'OrderingTable',
  props: {
    apiUrl: {type: String, required: true},
    downloadUrl: {type: String, required: true},
    hasCreatePermission: {type: Boolean, required: true},
  },
  data() {
    return {
      isBusy: false,
      error: false,
      advancedView: false,
      inactiveShown: false,
      yearFilter: [],
      fields: [
        {key: 'actions', label: 'Aktionen'},
        {key: 'vendor', label: 'Lieferant', sortable: true,
          filter: {type: 'string-contains', value: ''}},
        {key: 'employeeName', label: 'angefragt von', sortable: true,
          filter: {type: 'string-contains', value: ''}},
        {key: 'departments', label: 'Abteilungen', sortable: true, advancedView: true,
          filter: {type: 'multiselect', value: [], options: []},
        },
        {key: 'sapNumber', label: 'SAP-Nummer', advancedView: true, sortable: true,
          filter: {type: 'string-contains', value: ''}},
        {key: 'offersAmount', label: 'Angebote', advancedView: true, sortable: true,
          filter: {type: 'string-equals', value: ''}},
        {key: 'costs', label: '(vsl.) Gesamtkosten', sortable: true, class: 'col-costs',
          formatter: ({value}) => this.formatCosts(value), filterByFormatted: true,
          filter: {type: 'decimal-number-contains', value: ''}},
        {key: 'status', label: 'Status', class: 'col-status', formatter: ({value}) => this.formatStatus(value),
          sortable: true,
          sortByFormatted: ({value, item}) => Number(value).toString() + Number(item['statusDate']).toString(),
          filter: {type: 'multiselect', value: [],
            options: [
              {id: -3, name: 'fehlerhaft', icon: 'fa-solid fa-circle-exclamation'},
              {id: -2, name: 'nicht erhalten', icon: 'fa-solid fa-house-circle-xmark'},
              {id: -1, name: 'abgelehnt', icon: 'fa-solid fa-user-xmark'},
              {id: 0, name: 'in Erstellung', icon: 'fa-solid fa-user-pen'},
              {id: 1, name: 'eingereicht', icon: 'fa-solid fa-paper-plane'},
              {id: 2, name: 'freigegeben', icon: 'fa-solid fa-user-check'},
              {id: 3, name: 'bestellt', icon: 'fa-solid fa-truck-fast'},
              {id: 4, name: 'erhalten', icon: 'fa-solid fa-house-circle-check'},
              {id: 5, name: 'abgeschlossen', icon: 'fa-solid fa-check'},
            ],
          },
        },
        {key: 'comment', label: 'Kommentar', class: 'col-comment'},
        {key: 'orderedAt', label: 'Bestelldatum', advancedView: true, sortable: true,
          formatter: ({value}) => this.formatDate(value),
          filterByFormatted: true, filter: {type: 'string-contains', value: ''}},
        {key: 'deliveredAt', label: 'Lieferdatum', advancedView: true, sortable: true,
          formatter: ({value}) => this.formatDate(value),
          filterByFormatted: true, filter: {type: 'string-contains', value: ''}},
        {key: 'invoiceDate', label: 'Rechnungsdatum', advancedView: true, sortable: true,
          formatter: ({value}) => this.formatDate(value),
          filterByFormatted: true, filter: {type: 'string-contains', value: ''}},
      ],
      orderings: [],
    };
  },
  computed: {
    visibleFields() {
      if (this.advancedView) {
        return this.fields;
      } else {
        return this.fields.filter((field) => !field.advancedView);
      }
    },
    filteredOrderings() {
      return this.orderings.filter((ordering) => {
        // throw out orderings that are not in year filter
        if (this.yearFilter.length) {
          if (!this.yearFilter.some((year) => ordering['year'] === year)) {
            return false;
          }
        }
        // go through all filters
        return this.fields.filter((field) => field.filter).every((field) => {
          const filter = field.filter;
          // filter for string fields
          if (filter.type === 'string-contains') {
            if (filter.value === '') return true;
            if (field.filterByFormatted) {
              return String(field.formatter({value: ordering[field.key]})).includes(filter.value.toString());
            } else {
              return String(ordering[field.key]).toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '')
                  .includes(filter.value.toString().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, ''));
            }
          }
          if (filter.type === 'string-equals') {
            if (filter.value === '') {
              return true;
            }
            if (field.filterByFormatted) {
              return String(field.formatter({value: ordering[field.key]})) === (filter.value.toString());
            } else {
              return String(ordering[field.key]).toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '') ===
                  filter.value.toString().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
            }
          }
          if (filter.type === 'decimal-number-contains') {
            if (filter.value === '') {
              return true;
            }
            if (field.filterByFormatted) {
              if (filter.value.includes('.')) {
                return field.formatter({value: ordering[field.key]})
                    .includes(filter.value);
              } else {
                return field.formatter({value: ordering[field.key]}).replace(/\./g, '')
                    .includes(filter.value);
              }
            }
          }
          // filter for multiselects
          if (filter.type === 'multiselect') {
            if (!filter.value.length) {
              return true;
            }
            return filter.value.some((filter) => {
              return Array.isArray(ordering[field.key])
                  ? ordering[field.key].includes(filter.id)
                  : ordering[field.key] === filter.id;
            });
          }
        });
      });
    },
    invoiceYears() {
      // compute start year and current year
      const startYear = Math.min(...this.orderings.map((item) => item['year']));
      const currentYear = new Date().getFullYear();
      // return years as string array
      const years = [];
      for (let i = 0; i <= currentYear-startYear; i++) {
        years[i] = startYear+i;
      }
      return years.map((year) => year.toString());
    },
    invoiceSum() {
      return this.filteredOrderings
          .map((ordering) => Number(ordering['costs']))
          .reduce((sum, amount) => sum + amount, 0);
    },
    activeStatusFilterValues() {
      return new Set(
          this.fields.find((field) => field.key === 'status')['filter'].options
              .filter((option) => option.id !== 5)
              .map((option) => option.id),
      );
    },
    isFilteredByActive() {
      const statusField = this.fields.find((field) => field.key === 'status');

      const currentStatusFilter = new Set(statusField.filter.value.map((filter) => filter.id));

      return currentStatusFilter.size === this.activeStatusFilterValues.size &&
          [...currentStatusFilter].every((x) => this.activeStatusFilterValues.has(x));
    },
  },
  watch: {
    'orderings'() {
      this.setDepartmentsFilterOptions();
    },
  },
  mounted() {
    this.fetchOrderings();
    this.calculateContainerHeight();
    window.addEventListener('resize', this.calculateContainerHeight);
  },
  methods: {
    fetchOrderings(showInactive = false) {
      this.isBusy = true;

      // reset error state
      this.error = false;

      this.inactiveShown = showInactive;

      axios.get(this.apiUrl, {
        params: {
          showInactive: showInactive,
        },
      }).then((response) => {
        this.orderings = response.data.items;
      }).catch((error) => {
        this.error = error;
        this.orderings = [];
      }).finally(() => {
        this.isBusy = false;
      });
    },
    downloadOrderings() {
      const idList = this.filteredOrderings.map((ordering) => ordering.id);

      axios({
        url: this.downloadUrl,
        method: 'POST',
        responseType: 'blob',
        data: {
          orderings: idList,
        },
      }).then((response) => {
        fileDownload(response.data, 'Bestellanfragen.xlsx');
      }).catch(() => {
        alert('Beim Erstellen der Datei ist ein Fehler aufgetreten!');
      });
    },
    filterByActive() {
      const statusField = this.fields.find((field) => field.key === 'status');

      if (!this.isFilteredByActive) {
        statusField.filter.value = statusField.filter.options.filter((option) => option.id !== 5);
      } else {
        statusField.filter.value = [];
      }
    },
    resetFilters() {
      this.fields.filter((field) => field.filter).forEach((field) => field.filter.value = '');
    },
    setDepartmentsFilterOptions() {
      const optionValues = new Set();
      this.orderings.forEach((ordering) => ordering['departments'].forEach((dep) => optionValues.add(dep)));
      this.fields.find((field) => field.key === 'departments')['filter'].options =
          Array.from(optionValues).map((dep) => ({id: dep, name: dep}));
    },
    formatStatus(status) {
      switch (status) {
        case -3: return 'Bestellung fehlerhaft';
        case -2: return 'nicht erhalten';
        case -1: return 'abgelehnt';
        case 0: return 'in Erstellung';
        case 1: return 'eingereicht';
        case 2: return 'freigegeben';
        case 3: return 'bestellt';
        case 4: return 'erhalten';
        case 5: return 'abgeschlossen';
      }
    },
    formatCosts(costs) {
      const formatter = new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'});
      return formatter.format(costs);
    },
    formatDate(date) {
      return null !== date ? moment(date).format('DD.MM.YYYY') : '';
    },
    toggleAdvancedView() {
      if (!this.advancedView) {
        $('.container').toggleClass('container container-fluid');
      } else {
        $('.container-fluid').toggleClass('container container-fluid');
      }
      this.advancedView = !this.advancedView;
    },
    calculateContainerHeight() {
      const table = $(this.$refs['table'].$el);
      const windowHeight = $(window).height();
      const offsetTop = table.offset().top;
      const cardBottomPadding = parseInt($('.card-body').css('paddingBottom'), 10);
      const cardBottomBorder = parseInt($('.card').css('borderBottomWidth'), 10);
      const footerHeight = $('footer').outerHeight(true);
      const tableHeight = windowHeight - offsetTop - cardBottomPadding - cardBottomBorder - footerHeight;
      table.parents('.table-container').height(tableHeight);
    },
  },
};
